import { FC, useCallback, useEffect, useRef } from "react";
import { createGesture } from "@ionic/react";

export interface TimingSliderProps {
    index: number;
    label: string;
    position: number; // 0-1 fraction
    timeLabel: string; // formatted time of day
    durationLabel?: string; // e.g. "46 MINS"
    onPositionChange: (index: number, newPosition: number) => void;
    containerHeight: number;
}

const TimingSlider: FC<TimingSliderProps> = ({
    index,
    label,
    position,
    timeLabel,
    durationLabel,
    onPositionChange,
    containerHeight,
}) => {
    const handleRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{
        startY: number;
        startPosition: number;
    } | null>(null);

    // Store latest values in refs so the gesture doesn't need to be recreated on every render
    const positionRef = useRef(position);
    positionRef.current = position;
    const containerHeightRef = useRef(containerHeight);
    containerHeightRef.current = containerHeight;
    const onPositionChangeRef = useRef(onPositionChange);
    onPositionChangeRef.current = onPositionChange;

    // Use Ionic's createGesture to handle touch drag and disable IonContent scroll
    useEffect(() => {
        const el = handleRef.current;
        if (!el) return;

        const gesture = createGesture({
            el,
            gestureName: `timing-slider-${index}`,
            direction: "y",
            threshold: 0,
            passive: false,
            disableScroll: true,
            onStart: (detail) => {
                dragRef.current = {
                    startY: detail.startY,
                    startPosition: positionRef.current,
                };
            },
            onMove: (detail) => {
                if (!dragRef.current || containerHeightRef.current <= 0) return;
                const deltaY = detail.currentY - dragRef.current.startY;
                const deltaPosition = deltaY / containerHeightRef.current;
                const newPosition = dragRef.current.startPosition + deltaPosition;
                onPositionChangeRef.current(index, newPosition);
            },
            onEnd: () => {
                dragRef.current = null;
            },
        });

        gesture.enable(true);

        return () => {
            gesture.destroy();
        };
    }, [index]);

    // Mouse handlers for web testing
    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragRef.current = {
                startY: e.clientY,
                startPosition: positionRef.current,
            };

            const onMouseMove = (ev: MouseEvent) => {
                if (!dragRef.current || containerHeightRef.current <= 0) return;
                const deltaY = ev.clientY - dragRef.current.startY;
                const deltaPosition = deltaY / containerHeightRef.current;
                const newPosition = dragRef.current.startPosition + deltaPosition;
                onPositionChangeRef.current(index, newPosition);
            };
            const onMouseUp = () => {
                dragRef.current = null;
                window.removeEventListener("mousemove", onMouseMove);
                window.removeEventListener("mouseup", onMouseUp);
            };
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        },
        [index]
    );

    const yPercent = position * 100;

    return (
        <div
            ref={handleRef}
            className="timing-slider"
            style={{ top: `${yPercent}%` }}
            onMouseDown={onMouseDown}
        >
            {/* Time label on the left */}
            <span className="timing-slider-time">{timeLabel}</span>

            {/* Horizontal line */}
            <div className="timing-slider-line" />

            {/* Label + drag handle on the right */}
            <div
                className="timing-slider-handle"
            >
                <div className="timing-slider-handle-content">
                    <span className="timing-slider-label">{label}</span>
                    {/* Duration label below the label */}
                    {durationLabel && (
                        <span className="timing-slider-duration">{durationLabel}</span>
                    )}
                </div>
                <span className="timing-slider-grip">&#9776;</span>
            </div>
        </div>
    );
};

export default TimingSlider;
